import * as React from "react";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { PILL_SIZES, resolveTone } from "@/components/ui/status-pill";

/**
 * The app's chip. With `tone` it is the design-guidelines §6 outlined pill —
 * `border-{tone}-200 bg-{tone}-50 text-{tone}-700` in one of the shared sizes
 * — which is what every hand-written `<span className="inline-flex …
 * rounded-full border …">` used to restate. `StatusPill` is the same pill
 * with a leading tone dot (for status on carded/table rows); `StatusLabel` is
 * the borderless dot + label for flat clickable rows. Do not box passive
 * metadata (dates, timestamps, counts on a clickable row) — §6 says that reads
 * as a button; render it as plain text.
 *
 * Without `tone` the legacy shadcn variants still apply, unchanged, for the
 * existing call sites that have not been moved onto a tone yet.
 */

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

/**
 * @param {string} [props.tone]  A status name (neutral | info | success |
 *   warning | danger | premium) or a hue from `lib/ui/tones.js` ("orange",
 *   "indigo", …), resolved exactly as StatusPill resolves it.
 * @param {keyof typeof PILL_SIZES}   [props.size]  default | sm | tag | count | score | header (with `tone` only)
 * @param {string} [props.variant]  With `tone`: "soft" drops the outline
 *   (tinted fill only), matching StatusPill's soft look. Without `tone`: the
 *   legacy shadcn variant.
 */
function Badge({ className, variant, tone, size = "default", ...props }) {
  const t = tone ? resolveTone(tone) : null;
  const recipe = t
    ? cn(
        "inline-flex items-center gap-1.5 rounded-full",
        PILL_SIZES[size] || PILL_SIZES.default,
        variant === "soft" ? cn(t.bg, t.text) : cn("border", t.pill),
      )
    : badgeVariants({ variant });

  return <span className={cn(recipe, className)} {...props} />;
}

export { Badge, badgeVariants };
